/**
 * Valores financeiros do cofinanciamento federal da APS
 * Fonte: Portaria GM/MS n. 3.493/2024 (atualizada por 6.907/2025 e 9.591/2025)
 * Espelho do backend para simulacao client-side
 */

export type Classificacao = 'Otimo' | 'Bom' | 'Suficiente' | 'Regular';

export interface ValoresPorClassificacao {
  Otimo: number;
  Bom: number;
  Suficiente: number;
  Regular: number;
}

/** Componente Vinculo e Acompanhamento Territorial (CVAT) - R$/equipe/mes */
export const VALORES_VINCULO: Record<string, ValoresPorClassificacao> = {
  eSF:     { Otimo: 8000,  Bom: 6000,  Suficiente: 4000, Regular: 2000 },
  eAP_30h: { Otimo: 4000,  Bom: 3000,  Suficiente: 2000, Regular: 1000 },
  eAP_20h: { Otimo: 3000,  Bom: 2250,  Suficiente: 1500, Regular: 750 },
};

/** Componente Qualidade/Indicadores - R$/equipe/mes (Portaria 9.591/2025) */
export const VALORES_QUALIDADE: Record<string, ValoresPorClassificacao> = {
  // eSF / eAP (C1-C7)
  eSF:          { Otimo: 8000,  Bom: 6000,  Suficiente: 4000, Regular: 2000 },
  eAP_30h:      { Otimo: 4000,  Bom: 3000,  Suficiente: 2000, Regular: 1000 },
  eAP_20h:      { Otimo: 3000,  Bom: 2250,  Suficiente: 1500, Regular: 750 },
  // eMulti (M1-M2)
  eMulti_Amp:   { Otimo: 9000,  Bom: 6750,  Suficiente: 4500, Regular: 2250 },
  eMulti_Comp:  { Otimo: 6000,  Bom: 4500,  Suficiente: 3000, Regular: 1500 },
  eMulti_Est:   { Otimo: 3000,  Bom: 2250,  Suficiente: 1500, Regular: 750 },
  // eSB 40h (B1-B6)
  eSB_I:        { Otimo: 2500,  Bom: 2000,  Suficiente: 1500, Regular: 1000 },
  eSB_II:       { Otimo: 3300,  Bom: 2500,  Suficiente: 1700, Regular: 1200 },
  // eSB 40h quilombola
  eSB_I_Quil:   { Otimo: 3750,  Bom: 3000,  Suficiente: 2250, Regular: 1500 },
  eSB_II_Quil:  { Otimo: 4950,  Bom: 3750,  Suficiente: 2550, Regular: 1800 },
  // eSB carga horaria diferenciada (Portaria 9.591/2025)
  eSB_I_30h:    { Otimo: 1875,  Bom: 1500,  Suficiente: 1125, Regular: 750 },
  eSB_I_20h:    { Otimo: 1250,  Bom: 1000,  Suficiente: 750,  Regular: 500 },
  // eCR - Consultorio na Rua (Portaria 9.591/2025)
  eCR_III:      { Otimo: 8000,  Bom: 6000,  Suficiente: 4000, Regular: 2000 },
  eCR_II:       { Otimo: 7000,  Bom: 5250,  Suficiente: 3500, Regular: 1750 },
  eCR_I:        { Otimo: 6000,  Bom: 4500,  Suficiente: 3000, Regular: 1500 },
  // eAPP - Atencao Primaria Prisional (Portaria 9.591/2025)
  eAPP_Amp_30h: { Otimo: 8000,  Bom: 6000,  Suficiente: 4000, Regular: 2000 },
  eAPP_Amp_20h: { Otimo: 6000,  Bom: 4500,  Suficiente: 3000, Regular: 1500 },
  eAPP_Ess_30h: { Otimo: 7000,  Bom: 5250,  Suficiente: 3500, Regular: 1750 },
  eAPP_Ess_20h: { Otimo: 5000,  Bom: 3750,  Suficiente: 2500, Regular: 1250 },
};

/** Componente Fixo (Equidade) - R$/equipe/mes por estrato IED */
export const VALORES_FIXO: Record<string, Record<number, number>> = {
  eSF:     { 1: 18000, 2: 16000, 3: 14000, 4: 12000 },
  eAP_30h: { 1: 10800, 2: 9600,  3: 8400,  4: 7200 },
  eAP_20h: { 1: 7200,  2: 6400,  3: 5600,  4: 4800 },
};

/**
 * Tipo equipe e-SUS (nu_ms) -> chave financeira
 * Subtipos dependem de dados do SCNES. Aqui usamos o subtipo mais comum.
 */
export const TIPO_EQUIPE_MAP: Record<string, string> = {
  '70': 'eSF',
  '76': 'eAP_30h',      // eAP 30h (padrao)
  '71': 'eSB_I',        // eSB Tipo I 40h (padrao)
  '72': 'eMulti_Amp',   // eMulti Ampliada (padrao)
  '73': 'eCR_III',      // eCR Modalidade III (padrao)
  '74': 'eAPP_Amp_30h', // eAPP Ampliada 30h (padrao)
};

/** Obter valor mensal */
export function getValorMensal(
  tabela: Record<string, ValoresPorClassificacao>,
  tipoEquipe: string,
  classificacao: Classificacao,
): number {
  return tabela[tipoEquipe]?.[classificacao] ?? 0;
}

/** Classificacoes ordenadas para slider */
export const CLASSIFICACOES: Classificacao[] = ['Regular', 'Suficiente', 'Bom', 'Otimo'];
